import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { Alert, Skeleton, Spin } from "antd";
import StripeHelper from "../../../../StripeHelper";
import StripePaymentIntentStatusObserver from "../../components/stripe/StripePaymentIntentStatusObserver";
import { AxiosError } from "axios";
import { axiosErrorHandler } from "../../../../swzutils";
import { useSearchParams } from "react-router-dom";
import { formatCurrency } from "../../components/FormattersTS";
// Stripe adds this as a search / query param when redirecting to the 'return_url'
export const SEARCH_PARAM_PAYMENT_INTENT = 'payment_intent';
export const SEARCH_PARAM_PAYMENT_INTENT_CLIENT_SECRET = 'payment_intent_client_secret';
const StripePaymentElement = React.forwardRef(({ returnUrl, onReady, onResult, onError }, exportedRef) => {
    const stripe = useStripe();
    const elements = useElements();
    useImperativeHandle(exportedRef, () => ({
        confirmPaymentWithStripe: () => {
            confirmPaymentWithStripe();
        }
    }));
    const confirmPaymentWithStripe = () => {
        if (!stripe || !elements) {
            // Stripe.js hasn't yet loaded.
            // Make sure to disable form submission until Stripe.js has loaded.
            return;
        }
        //console.log("confirmPayment returnUrl", returnUrl);
        stripe.confirmPayment({
            elements,
            redirect: "if_required",
            confirmParams: {
                return_url: window.location.origin + returnUrl
            },
        })
            .then(onResult)
            .catch(error => {
            console.error("error", error);
            onError(error.message);
        });
    };
    return (_jsx(PaymentElement, { onReady: onReady }));
});
const StripePaymentIntentForm = ({ onCreatePaymentIntent, returnUrl, onReady, onPaymentSuccess }, exportedRef) => {
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [paymentIntent, setPaymentIntent] = useState(null);
    const [paymentConfirmed, setPaymentConfirmed] = useState(false);
    const paymentElementRef = useRef(null);
    const [searchParams] = useSearchParams();
    useImperativeHandle(exportedRef, () => ({
        submit: () => {
            doSubmit();
        }
    }));
    useEffect(() => {
        if (searchParams.has(SEARCH_PARAM_PAYMENT_INTENT)) {
            setPaymentIntent({
                id: searchParams.get(SEARCH_PARAM_PAYMENT_INTENT),
                clientSecret: searchParams.get(SEARCH_PARAM_PAYMENT_INTENT_CLIENT_SECRET)
            });
            setPaymentConfirmed(true);
        }
        else {
            createPaymentIntent();
        }
    }, []);
    const createPaymentIntent = () => {
        onCreatePaymentIntent()
            .then(response => setPaymentIntent(response.data))
            .catch(errorHandler);
    };
    const errorHandler = (error) => {
        axiosErrorHandler(error, (errorMessage) => {
            setLoading(false);
            setErrorMessage(errorMessage);
        });
    };
    const doSubmit = () => {
        setLoading(true);
        setErrorMessage(null);
        paymentElementRef.current?.confirmPaymentWithStripe();
    };
    function onPaymentResult(result) {
        //console.log("paymentResult", result);
        if (result.error) {
            setErrorMessage(result.error.message);
            setLoading(false);
        }
        else {
            setPaymentConfirmed(true);
        }
    }
    return (_jsx("div", { style: { minHeight: '180px' }, children: _jsxs(Spin, { spinning: loading, children: [_jsxs(Skeleton, { active: true, loading: !paymentIntent, children: [paymentIntent?.paymentAmountIncTax &&
                            _jsxs("h3", { children: ["Amount Due: ", formatCurrency(paymentIntent.paymentAmountIncTax), " ", _jsx("small", { children: "(inc GST)" })] }), paymentIntent &&
                            _jsx(Elements, { stripe: StripeHelper.stripePromise, options: {
                                    clientSecret: paymentIntent.clientSecret ?? undefined,
                                    customerSessionClientSecret: paymentIntent.customerSessionSecret
                                }, children: paymentConfirmed
                                    ? _jsx(StripePaymentIntentStatusObserver, { paymentIntent: paymentIntent.id, paymentIntentClientSecret: paymentIntent.clientSecret, onPaymentSuccess: () => {
                                            setPaymentConfirmed(false);
                                            setPaymentIntent(null);
                                            setErrorMessage(null);
                                            onPaymentSuccess();
                                        }, onPaymentFailure: () => setLoading(false) })
                                    :
                                        _jsx(StripePaymentElement, { ref: paymentElementRef, returnUrl: returnUrl, onReady: onReady, onResult: onPaymentResult, onError: errMsge => {
                                                setLoading(false);
                                                setErrorMessage(errMsge);
                                            } }) })] }), errorMessage &&
                    _jsx(Alert, { type: "error", title: errorMessage, banner: true })] }) }));
};
export default React.forwardRef(StripePaymentIntentForm);
